/**
 * Projected average rating after removing selected reviews from the profile.
 */
export function calculateProjectedRating(totalReviews, averageRating, selectedReviews) {
  const total = Number(totalReviews) || 0;
  const avg = Number(averageRating) || 0;

  if (total <= 0) {
    return { projected: 0, delta: 0, remainingCount: 0 };
  }

  const selected = Array.isArray(selectedReviews) ? selectedReviews : [];
  const removeCount = selected.length;

  if (removeCount === 0) {
    return {
      projected: avg,
      delta: 0,
      remainingCount: total,
    };
  }

  const sum = avg * total;
  const removeSum = selected.reduce((acc, review) => acc + (Number(review.rating) || 0), 0);
  const newTotal = total - removeCount;

  if (newTotal <= 0) {
    return { projected: 5, delta: 5 - avg, remainingCount: 0 };
  }

  const projected = Math.min(5, (sum - removeSum) / newTotal);
  return {
    projected,
    delta: projected - avg,
    remainingCount: newTotal,
  };
}

export function getRatingVisualTier(score) {
  const value = Number(score) || 0;

  if (value >= 4.6) {
    return {
      textClass: "text-secondary",
      bgClass: "bg-secondary",
      message:
        "Excellent! Resolving these reviews would place you in a top-tier reputation range.",
    };
  }
  if (value >= 4.4) {
    return {
      textClass: "text-yellow-500",
      bgClass: "bg-yellow-500",
      message: "Strong progress — you are approaching a trusted partner tier.",
    };
  }
  if (value >= 4.2) {
    return {
      textClass: "text-orange-500",
      bgClass: "bg-orange-500",
      message: "Improving. Resolving selected reviews would lift you toward industry average.",
    };
  }
  return {
    textClass: "text-error",
    bgClass: "bg-error",
    message:
      "Your rating needs attention. Critical reviews are negatively impacting profile performance.",
  };
}

export function formatRatingDisplay(value) {
  return (Number(value) || 0).toFixed(1);
}

export function formatDeltaDisplay(delta) {
  const n = Number(delta) || 0;
  const prefix = n >= 0 ? "+" : "";
  return `${prefix}${n.toFixed(2)}`;
}

/** Star fill state for 1–5 display from a decimal score. */
export function starStatesForScore(score) {
  const s = Math.min(5, Math.max(0, Number(score) || 0));
  return [1, 2, 3, 4, 5].map((index) => {
    if (s >= index) return "full";
    if (s > index - 1) return "half";
    return "empty";
  });
}
